import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import {
  AVAILABLE_CULTURES,
  CookieService,
  CultureLanguageMap,
  LocalStorageService,
  WindowRef,
} from '@boels/helpers';
import { StorageKey } from '@boels/models';
import {
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { SiteContextFacade } from '../../site-context/facade/site-context.facade';
import { CultureFacade } from '../../cultures/facade/cultures.facade';
import { RedirectService } from '../../redirect/redirect.service';

export const CultureGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  return canActivate(route, state);
};

const canActivate = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const splitRoute: string[] = getSplitRouter(state);
  const culture: string | null = tryGetValidCulture(splitRoute);

  if (!culture) {
    inject(Router).navigateByUrl(redirectToCountrySelection(state), {
      replaceUrl: true,
    });
    return of(false);
  }

  return handleValidCulture(route, splitRoute, culture, state.url);
};

const getSplitRouter = (state: RouterStateSnapshot) => {
  let url = state.url;
  if (url.includes('#')) {
    url = url.split('#')[0];
  }
  return url.split(/\/|\?/).filter(Boolean);
};

const handleValidCulture = (
  route: ActivatedRouteSnapshot,
  splitRoute: string[],
  culture: string,
  url: string,
): Observable<boolean | UrlTree> => {
  const cultureFacade = inject(CultureFacade);
  const siteContextFacade = inject(SiteContextFacade);
  const redirectService = inject(RedirectService);
  const router = inject(Router);

  const language =
    CultureLanguageMap[
      culture.toLowerCase() as keyof typeof CultureLanguageMap
    ];

  return cultureFacade.getActiveLanguage().pipe(
    switchMap((currentLanguage: string) => {
      if (currentLanguage !== language) {
        cultureFacade.setActiveLanguage(language);
        siteContextFacade.loadBaseSites();
      }
      return siteContextFacade.getAllIsocodes().pipe(
        filter(Boolean),
        withLatestFrom(cultureFacade.getActiveCulture()),
        map(([cultures, activeCulture]) => {
          const cultureFoundInCultures = cultures.includes(culture);

          if (cultureFoundInCultures && splitRoute.includes(culture)) {
            if (activeCulture !== culture) {
              cultureFacade.setActiveContext(culture);
            }
            return true;
          }

          if (cultureFoundInCultures) {
            redirectService.noCaching();
            const queryParams = route.queryParams;
            const urlParts = url.split('?')[0].split(/\/|\?/).filter(Boolean);
            router.navigateByUrl(
              router.createUrlTree([culture, ...urlParts], {
                queryParamsHandling: 'merge',
                queryParams,
              }),
              { replaceUrl: true },
            );
            return false;
          }

          router.navigateByUrl(router.createUrlTree(['/countryselection']), {
            replaceUrl: true,
          });
          return false;
        }),
      );
    }),
    take(1),
  );
};

const tryGetValidCulture = (splitRoute: string[]): string | null => {
  const cultureInUrl: string | null = getValidCulture(
    splitRoute?.find(Boolean),
  );

  if (cultureInUrl) return cultureInUrl;

  let cultureInStorageOrCookie: string | null;

  if (inject(WindowRef).isBrowser()) {
    const storageCulture = getValidCulture(
      inject(LocalStorageService).getItem(StorageKey.CULTURE),
    );
    const cookieCulture = getValidCulture(
      inject(CookieService).getCookie(StorageKey.CULTURE),
    );

    cultureInStorageOrCookie = storageCulture || cookieCulture;
  } else {
    cultureInStorageOrCookie = getValidCulture(
      inject(CookieService).getCookie(StorageKey.CULTURE),
    );
  }

  return cultureInStorageOrCookie || null;
};

const redirectToCountrySelection = (state: RouterStateSnapshot): UrlTree => {
  const urlParts = state.url.split('?');
  const pathSegments = urlParts[0].split('/').filter(Boolean);

  const filteredSegments = pathSegments.filter(
    (segment) => !isCulterSegment(segment),
  );
  const redirectRoute = filteredSegments.join('/');

  const navigationExtras = {
    queryParams: {
      ...state.root.queryParams,
    },
  };
  if (redirectRoute)
    navigationExtras.queryParams['redirect'] = `/${encodeURIComponent(
      redirectRoute,
    )}`;

  inject(RedirectService).noCaching();
  return inject(Router).createUrlTree(['/countryselection'], navigationExtras);
};

const getValidCulture = (culture: string | undefined): string | null => {
  return culture && AVAILABLE_CULTURES.includes(culture) ? culture : null;
};

const isCulterSegment = (segment: string): boolean => {
  const regex = new RegExp(`^[a-z]{2}-[a-z]{2}$`);
  return regex.test(segment);
};
